/**
 * Returns true if the given value is a primitive (string, number, boolean, undefined, or null).
 *
 * @param prop - The value to check.
 * @returns `true` if the value is a primitive.
 */
export const isPrimitive = (prop) => typeof prop === 'string' ||
    typeof prop === 'number' ||
    typeof prop === 'boolean' ||
    prop === undefined ||
    prop === null;
/**
 * Super simple shallow diff which simply compares values in object a with values in object b.
 * Returns an object containing only the keys where the values differ (or new keys in b).
 *
 * @param a - The original object.
 * @param b - The updated object.
 * @returns An object containing changed and added keys from b.
 */
export const shallowDiff = (a, b) => {
    if (!a || !b || typeof a !== 'object' || typeof b !== 'object') {
        throw Error('Invalid arguments given to shallow-diff');
    }
    const changes = Object.keys(a).reduce((reduction, key) => (a[key] === b[key] ? reduction : { ...reduction, [key]: b[key] }), {});
    const additions = Object.keys(b).reduce((reduction, key) => (a[key] === undefined ? { ...reduction, [key]: b[key] } : reduction), {});
    return {
        ...changes,
        ...additions,
    };
};
/**
 * Compare two values. Returns true if they are different.
 * Uses `shallowDiff` for objects, identity comparison for primitives.
 *
 * @param a - The first value to compare.
 * @param b - The second value to compare.
 * @returns `true` if the values are different, `false` if they are the same.
 */
export const shallowCompare = (a, b) => {
    if (isPrimitive(a) || isPrimitive(b)) {
        return a !== b;
    }
    return Object.keys(shallowDiff(a, b)).length > 0;
};
